/**
 * The Inventory list in the page's address: its page, how many a page
 * shows (the pager's choice, sent as the query's `limit`) and its sales-tax
 * filter. The page reads it through useUrlState and its route
 * loader parses it the same way, so the loader prefetches exactly the list
 * the page then asks for: one cache entry, one request. (The loader once
 * read the page by hand, and asked for page -3 where the page asked for 1.)
 * Each value is the wire query's own field; an address the wire would
 * refuse falls back to its default instead. The search box's `q` stays
 * with useSearchInput, which debounces and sanitizes it.
 *
 * There is one list, every item on it, companions too (docs/plans/
 * companions.md, Decision 28); the nested and flat views it once had are gone.
 */
import { z } from 'zod'
import { PAGE_SIZE_DEFAULT, PageSizeChoiceParam } from '@gearghost/api-schemas/_primitives'
import { GetInventoryItemsQuerySchema } from '@gearghost/api-schemas/inventory'
import { INVENTORY_TAX_PARAM } from './inventoryTaxFilter'

const { shape: wire } = GetInventoryItemsQuerySchema

export const inventoryUrlSchema = z.object({
  page: wire.page.unwrap().catch(1),
  perPage: PageSizeChoiceParam,
  [INVENTORY_TAX_PARAM]: wire.tax.unwrap().catch(''),
})
export type InventoryListAddress = z.infer<typeof inventoryUrlSchema>

export const INVENTORY_URL_DEFAULTS: InventoryListAddress = { page: 1, perPage: PAGE_SIZE_DEFAULT, tax: '' }

/** The list the address holds, as the route loader asks for it: one value per name, the last one winning, as useUrlState reads it. */
export const inventoryListAddressOf = (params: URLSearchParams): InventoryListAddress =>
  inventoryUrlSchema.parse(Object.fromEntries(params))
